import { CheckCircle, Info, Warning, Error as ErrorIcon, ChevronRight } from '@mui/icons-material';
import { Chip, Divider, Stack, Typography } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';

import type { NotificationInterface } from 'types';
import { toTitleCase } from 'utils/v2/text';

interface Props {
    notification: NotificationInterface;
    onNotificationClick: (notification: NotificationInterface) => void;
    showDivider?: boolean;
}

const getTypeIcon = (type: string | null) => {
    switch (type) {
        case 'SUCCESS':
            return <CheckCircle color="success" sx={{ fontSize: 18 }} />;
        case 'WARNING':
            return <Warning color="warning" sx={{ fontSize: 18 }} />;
        case 'ERROR':
            return <ErrorIcon color="error" sx={{ fontSize: 18 }} />;
        default:
            return <Info color="info" sx={{ fontSize: 18 }} />;
    }
};

const getTypeColor = (
    type: string | null
): 'success' | 'warning' | 'error' | 'info' | 'default' => {
    switch (type) {
        case 'SUCCESS':
            return 'success';
        case 'WARNING':
            return 'warning';
        case 'ERROR':
            return 'error';
        case 'INFO':
            return 'info';
        default:
            return 'default';
    }
};

const getIconBg = (type: string | null) => {
    switch (type) {
        case 'SUCCESS':
            return 'success.lighter';
        case 'WARNING':
            return 'warning.lighter';
        case 'ERROR':
            return 'error.lighter';
        default:
            return 'info.lighter';
    }
};

const hasMetadata = (n: NotificationInterface) =>
    n.notification_type || n.program_name || n.client_status || (n.firstname && n.lastname);

const NotificationCard = ({ notification, onNotificationClick, showDivider = true }: Props) => {
    const showMeta = hasMetadata(notification);
    const timeAgo = formatDistanceToNow(new Date(notification.created_at), { addSuffix: true });
    const isClickable = Boolean(notification.url);

    return (
        <>
            <Stack
                direction="row"
                spacing={{ xs: 1.25, sm: 1.5 }}
                onClick={() => onNotificationClick(notification)}
                role={isClickable ? 'button' : undefined}
                sx={{
                    alignItems: 'flex-start',
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 1.25, sm: 1.5 },
                    cursor: isClickable ? 'pointer' : 'default',
                    bgcolor: 'transparent',
                    transition: 'background-color 0.15s',
                    '&:hover': isClickable ? { bgcolor: 'action.hover' } : undefined,
                    '&:active': isClickable ? { bgcolor: 'action.selected' } : undefined,
                }}
            >
                <Stack
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'center',
                        mt: 0.15,
                        flexShrink: 0,
                        width: { xs: 34, sm: 38 },
                        height: { xs: 34, sm: 38 },
                        borderRadius: '50%',
                        bgcolor: getIconBg(notification.notification_type),
                    }}
                >
                    {getTypeIcon(notification.notification_type)}
                </Stack>

                <Stack sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
                        <Typography
                            variant="body2"
                            sx={{
                                flex: 1,
                                fontWeight: 500,
                                lineHeight: 1.45,
                                color: 'text.primary',
                                wordBreak: 'break-word',
                            }}
                        >
                            {notification.message}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                display: { xs: 'none', sm: 'block' },
                                flexShrink: 0,
                                color: 'text.disabled',
                                whiteSpace: 'nowrap',
                                fontSize: '0.7rem',
                                pt: 0.15,
                            }}
                        >
                            {timeAgo}
                        </Typography>
                        {isClickable && (
                            <ChevronRight
                                sx={{
                                    display: { xs: 'none', sm: 'block' },
                                    fontSize: 18,
                                    color: 'text.disabled',
                                    flexShrink: 0,
                                    mt: 0.15,
                                }}
                            />
                        )}
                    </Stack>

                    <Stack
                        direction="row"
                        useFlexGap
                        spacing={0.75}
                        sx={{
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            display: showMeta ? 'flex' : { xs: 'flex', sm: 'none' },
                            mt: 0.75,
                        }}
                    >
                        {notification.notification_type && (
                            <Chip
                                label={toTitleCase(notification.notification_type)}
                                size="small"
                                color={getTypeColor(notification.notification_type)}
                                variant="outlined"
                                sx={{ height: 22, fontSize: '0.675rem', fontWeight: 500 }}
                            />
                        )}
                        {notification.program_name && (
                            <Chip
                                label={notification.program_name}
                                size="small"
                                variant="outlined"
                                sx={{ height: 22, fontSize: '0.675rem' }}
                            />
                        )}
                        {notification.client_status && (
                            <Chip
                                label={toTitleCase(notification.client_status)}
                                size="small"
                                sx={{ height: 22, fontSize: '0.675rem' }}
                            />
                        )}
                        <Typography
                            variant="caption"
                            sx={{
                                display: { xs: 'block', sm: 'none' },
                                color: 'text.disabled',
                                fontSize: '0.7rem',
                                ml: showMeta ? 'auto' : 0,
                            }}
                        >
                            {timeAgo}
                        </Typography>
                    </Stack>
                </Stack>
            </Stack>
            {showDivider && <Divider sx={{ mx: { xs: 1.5, sm: 2 } }} />}
        </>
    );
};

export default NotificationCard;
